import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  UserGroupIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  XMarkIcon,
  PlusIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface PaymentBatch {
  id: string;
  batchNumber: string;
  totalEmployees: number;
  totalAmount: number;
  status: 'draft' | 'submitted' | 'approved' | 'processed' | 'completed' | 'rejected';
  submissionDate: string;
  processingDate?: string;
  completionDate?: string;
  submittedBy: string;
  approvedBy?: string;
  rejectionReason?: string;
  description?: string;
  paymentMethod: 'bank_transfer' | 'cash' | 'check';
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface BatchesTabProps {
  batches: PaymentBatch[];
  selectedBatch: PaymentBatch | null;
  setSelectedBatch: (batch: PaymentBatch | null) => void;
  showCreateBatch: boolean;
  setShowCreateBatch: (show: boolean) => void;
}

const BatchesTab: React.FC<BatchesTabProps> = ({
  batches,
  selectedBatch,
  setSelectedBatch,
  showCreateBatch,
  setShowCreateBatch
}) => {
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'employees' | 'status'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400';
      case 'approved':
      case 'processed':
        return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
      case 'submitted':
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'rejected':
        return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'draft':
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
      default:
        return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'approved':
      case 'processed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'submitted':
        return <ClockIcon className="w-4 h-4" />;
      case 'rejected':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'draft':
        return <DocumentTextIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'مكتملة';
      case 'approved': return 'معتمدة';
      case 'processed': return 'قيد المعالجة';
      case 'submitted': return 'مرسلة';
      case 'rejected': return 'مرفوضة';
      case 'draft': return 'مسودة';
      default: return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'high': return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400';
      case 'low': return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter and sort batches
  const filteredBatches = batches
    .filter(batch => filterStatus === 'all' || batch.status === filterStatus)
    .sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.submissionDate).getTime();
          bValue = new Date(b.submissionDate).getTime();
          break;
        case 'amount':
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        case 'employees':
          aValue = a.totalEmployees;
          bValue = b.totalEmployees;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = new Date(a.submissionDate).getTime();
          bValue = new Date(b.submissionDate).getTime();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
      }
    });

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="sm:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">جميع الحالات</option>
                <option value="draft">مسودة</option>
                <option value="submitted">مرسلة</option>
                <option value="approved">معتمدة</option>
                <option value="processed">قيد المعالجة</option>
                <option value="completed">مكتملة</option>
                <option value="rejected">مرفوضة</option>
              </select>
            </div>

            <div className="sm:w-48">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field as any);
                  setSortOrder(order as any);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="date-desc">التاريخ (الأحدث)</option>
                <option value="date-asc">التاريخ (الأقدم)</option>
                <option value="amount-desc">المبلغ (الأعلى)</option>
                <option value="amount-asc">المبلغ (الأقل)</option>
                <option value="employees-desc">عدد الموظفين (الأكثر)</option>
                <option value="employees-asc">عدد الموظفين (الأقل)</option>
              </select>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateBatch(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <PlusIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
            دفعة جديدة
          </motion.button>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            عرض {filteredBatches.length} من أصل {batches.length} دفعة
          </p>
        </div>
      </div>

      {/* Batches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBatches.map((batch, index) => (
          <motion.div
            key={batch.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-shadow duration-300"
          >
            <div className="p-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {batch.batchNumber}
                </h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                  {getStatusIcon(batch.status)}
                  <span className="mr-1 rtl:mr-0 rtl:ml-1">
                    {getStatusText(batch.status)}
                  </span>
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <UserGroupIcon className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-xs text-gray-600 dark:text-gray-400">الموظفون</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {batch.totalEmployees.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <BanknotesIcon className="h-5 w-5 text-green-500 mx-auto mb-1" />
                  <p className="text-xs text-gray-600 dark:text-gray-400">المبلغ</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatCurrency(batch.totalAmount)}
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <CalendarDaysIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                  <span>تاريخ الإرسال: {formatDate(batch.submissionDate)}</span>
                </div>
                {batch.completionDate && (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    <span>تاريخ الإكمال: {formatDate(batch.completionDate)}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  مرسل بواسطة: <span className="font-medium">{batch.submittedBy}</span>
                </p>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedBatch(batch)}
                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                >
                  <EyeIcon className="h-4 w-4" />
                </motion.button>
                {batch.status === 'draft' && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </motion.button>
                  </>
                )}
              </div>
              
              {batch.status === 'draft' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-md transition-all duration-200"
                >
                  <ArrowUpTrayIcon className="h-3 w-3 mr-1 rtl:mr-0 rtl:ml-1" />
                  إرسال
                </motion.button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {filteredBatches.length === 0 && (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            لا توجد دفعات
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            لم يتم العثور على دفعات تطابق المعايير المحددة
          </p>
        </div>
      )}
    </div>
  );
};

export default BatchesTab;
